import { useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { get, isEmpty } from "lodash";
import moment from "moment";

import execAuthenticatedFlow from "./flow/execAuthenticatedFlow";
import execAnonymousFlow from "./flow/execAnonymousFlow";

const generateParams = (survey, params) => {
  // Depending on the survey status preserve only those parameters that are allowed in each mode
  const production = get(survey, 'status') === 'published';
  const allowedParams = production ? {
    surveyType: params.surveyType,
  } : {
    surveyType: params.surveyType,
    pageId: params.pageId,
    questionId: params.questionId,
    blockDisplayLogic: params.blockDisplayLogic,
    questionDisplayLogic: params.questionDisplayLogic,
    questionRequiredLogic: params.questionRequiredLogic,
  };

  return allowedParams;
};

const useUserFlow = (
  initSurvey,
  metadata,
  params,
  locale,
  branch,
  doSetParams,
  doImportQuestionnaire,
  doImportUser
) => {
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [showOutroPage, setShowOutroPage] = useState(false);
  const [showClosingPage, setShowClosingPage] = useState(false);
  const [prepareQuestionnaire, setPrepareQuestionnaire] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [survey, setSurvey] = useState(initSurvey);

  useEffect(() => {
    if (!isEmpty(survey) && !isEmpty(survey._id)) {
      const allowedParams = generateParams(survey, params);
      const production = get(survey, 'status') === 'published';

      if (production) {
        // Show Closed page if current date is out of range
        const currentDate = Date.now();
        if (moment(currentDate).isBetween(survey.startDate, survey.endDate)) {
          setShowLandingPage(true);
        } else {
          setShowClosingPage(true);
        }
      } else {
        // Check if pageId is overriden
        const pageId = get(allowedParams, 'pageId');
        const questionId = get(allowedParams, 'questionId');

        if (pageId === 'INTRO') setShowLandingPage(true);
        else if (pageId === 'OUTRO') setShowOutroPage(true);
        else if (pageId === 'CLOSED') setShowClosingPage(true);
        else if (questionId) setPrepareQuestionnaire(true);
        else setShowLandingPage(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey]);

  useEffect(() => {
    (async () => {
      let responseId = null;
      let payload = {};
      let version = {};
      let history = {};

      if (prepareQuestionnaire) {
        setLoader(true);

        try {
          if (survey.type === "private") {
            const data = await execAuthenticatedFlow(
              survey,
              metadata,
              locale,
              branch
            );

            responseId = get(data, "responseId");
            version = get(data, "version", {});
            payload = get(data, "payload", {});
            history = get(data, "history", {});

            doImportUser();
          } else if (survey.type === "public") {
            const data = await execAnonymousFlow(
              survey,
              metadata,
              locale,
              branch
            );

            responseId = get(data, "responseId");
            version = get(data, "version", {});
            payload = get(data, "payload", {});

            setIsAnonymous(true);
          } else if (survey.type === "hybrid") {
            // Depending on whether the user is signed in or not, execute the corresponding flow
            const cognitoUser = await Auth.currentUserInfo();
            const execFlow = cognitoUser ? execAuthenticatedFlow : execAnonymousFlow;

            const data = await execFlow(
              survey,
              metadata,
              locale,
              branch
            );

            responseId = get(data, "responseId");
            version = get(data, "version", {});
            payload = get(data, "payload", {});
            history = get(data, "history", {});

            if (cognitoUser) {
              doImportUser();
            } else {
              setIsAnonymous(true);
            }
          } else {
            throw new Error(`Unknown survey type: ${survey.type}`);
          }

          // Fetch a localized version of the questionnaire
          const surveyId = get(survey, "_id");
          const questionnaire = await API.get(
            "surveys",
            `/surveys/${surveyId}/questions/locale/${locale}`
          );

          const allowedParams = generateParams(survey, params);
          doSetParams(allowedParams);

          doImportQuestionnaire({
            blocks: survey.blocks,
            questions: questionnaire.questions,
            version,
            slug: surveyId,
            responseId,
            payload,
            history,
            params: allowedParams,
          });

          setPrepareQuestionnaire(false);
          setShowQuestionnaire(true);
        } catch (error) {
          setErrorMessage(error.message || error);
        } finally {
          setLoader(false);
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareQuestionnaire]);

  const doPrepareQuestionnaire = () => {
    setShowLandingPage(false);
    setPrepareQuestionnaire(true);
  };

  const doExecFlow = surveyDoc => setSurvey(surveyDoc);

  return {
    showLandingPage,
    showOutroPage,
    showClosingPage,
    showQuestionnaire,
    flowLoader: loader,
    flowError: errorMessage,
    isAnonymous,
    doClearFlowError: () => setErrorMessage(null),
    doExecFlow,
    doPrepareQuestionnaire
  };
};

export default useUserFlow;
